import React from 'react';
import { Gamepad2, Star } from 'lucide-react';

const translations = {
  de: {
    title: 'Unsere beliebtesten Programme',
    subtitle: 'Entdecken Sie Trainings- und Ernaehrungsplaene fuer unterschiedliche Ziele',
    games: [
      { name: 'Lean Start 4 Wochen', category: 'Fettabbau', rating: 4.8 },
      { name: 'Strength Builder', category: 'Muskelaufbau', rating: 4.7 },
      { name: 'Core & Mobility', category: 'Beweglichkeit', rating: 4.9 },
      { name: 'Cardio Boost', category: 'Ausdauer', rating: 4.6 },
      { name: 'Vegan Power Plan', category: 'Ernaehrung', rating: 4.8 },
      { name: 'Vegetarian Balance', category: 'Ernaehrung', rating: 4.5 },
      { name: 'Meal Prep Smart', category: 'Rezepte', rating: 4.7 },
      { name: 'Performance Reset', category: 'Ganzkoerper', rating: 4.9 },
    ],
  },
  en: {
    title: 'Our Most Popular Programs',
    subtitle: 'Explore training and nutrition plans for different goals',
    games: [
      { name: 'Lean Start 4 Weeks', category: 'Fat Loss', rating: 4.8 },
      { name: 'Strength Builder', category: 'Muscle Gain', rating: 4.7 },
      { name: 'Core & Mobility', category: 'Mobility', rating: 4.9 },
      { name: 'Cardio Boost', category: 'Endurance', rating: 4.6 },
      { name: 'Vegan Power Plan', category: 'Nutrition', rating: 4.8 },
      { name: 'Vegetarian Balance', category: 'Nutrition', rating: 4.5 },
      { name: 'Meal Prep Smart', category: 'Recipes', rating: 4.7 },
      { name: 'Performance Reset', category: 'Full Body', rating: 4.9 },
    ],
  }
};

const gameColors = [
  { bg: 'from-emerald-600 to-teal-700', accent: 'bg-emerald-400' },
  { bg: 'from-amber-600 to-orange-700', accent: 'bg-amber-400' },
  { bg: 'from-purple-600 to-violet-700', accent: 'bg-purple-400' },
  { bg: 'from-blue-600 to-cyan-700', accent: 'bg-blue-400' },
  { bg: 'from-rose-600 to-pink-700', accent: 'bg-rose-400' },
  { bg: 'from-indigo-600 to-blue-700', accent: 'bg-indigo-400' },
  { bg: 'from-fuchsia-600 to-purple-700', accent: 'bg-fuchsia-400' },
  { bg: 'from-yellow-500 to-orange-600', accent: 'bg-yellow-400' },
];

const gameIcons = ['🏋️', '💪', '🧘', '🔥', '🥗', '🥬', '🍽️', '⚡'];

function Games({ language }) {
  const t = translations[language];

  return (
    <section id="games" className="relative py-24 overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 grid-pattern opacity-50"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 border border-accent/30 rounded-full mb-6">
            <Gamepad2 className="w-5 h-5 text-accent" />
            <span className="text-accent text-sm font-body font-semibold tracking-wide">AI PROGRAMS</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-4">
            {t.title}
          </h2>
          <p className="text-slate-400 font-body text-lg max-w-2xl mx-auto">
            {t.subtitle}
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-primary via-secondary to-accent mx-auto rounded-full mt-6"></div>
        </div>

        {/* Games Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
          {t.games.map((game, index) => {
            const color = gameColors[index];
            const icon = gameIcons[index];
            
            return (
              <div
                key={index}
                className="game-card group relative rounded-2xl overflow-hidden"
              >
                {/* Card Background */}
                <div className={`aspect-[3/4] bg-gradient-to-br ${color.bg} p-4 flex flex-col justify-between relative`}>
                  {/* Game Icon */}
                  <div className="flex-1 flex items-center justify-center">
                    <span className="text-6xl sm:text-7xl transform group-hover:scale-110 transition-transform duration-300 drop-shadow-lg">
                      {icon}
                    </span>
                  </div>
                  
                  {/* Game Info */}
                  <div className="relative z-10">
                    <div className="flex items-center gap-1 mb-2">
                      <Star className="w-4 h-4 text-yellow-300 fill-yellow-300" />
                      <span className="text-white text-sm font-body font-semibold">{game.rating}</span>
                    </div>
                    <h3 className="text-white font-heading font-bold text-sm sm:text-base mb-1 truncate">
                      {game.name}
                    </h3>
                    <span className="text-white/70 text-xs font-body uppercase tracking-wider">
                      {game.category}
                    </span>
                  </div>

                  {/* Decorative elements */}
                  <div className={`absolute top-3 right-3 w-2 h-2 ${color.accent} rounded-full`}></div>
                  <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-black/30 to-transparent"></div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom decoration - Device showcase */}
        <div className="mt-20 relative">
          <div className="text-center mb-8">
            <p className="text-slate-500 font-body text-sm uppercase tracking-widest">
              {language === 'de' ? 'Nutzen Sie FitXcel auf jedem Geraet' : 'Use FitXcel on any device'}
            </p>
          </div>
          <div className="flex justify-center items-end gap-4 sm:gap-8">
            {/* Mobile */}
            <div className="w-16 h-28 sm:w-20 sm:h-36 bg-surface rounded-xl border-2 border-slate-700 flex items-center justify-center shadow-lg shadow-primary/20 animate-float">
              <span className="text-2xl sm:text-3xl">📱</span>
            </div>
            {/* Tablet */}
            <div className="w-24 h-32 sm:w-32 sm:h-44 bg-surface rounded-2xl border-2 border-slate-700 flex items-center justify-center shadow-lg shadow-secondary/20 animate-float animation-delay-200">
              <span className="text-3xl sm:text-4xl">📲</span>
            </div>
            {/* Laptop */}
            <div className="w-32 h-24 sm:w-44 sm:h-32 bg-surface rounded-xl border-2 border-slate-700 flex items-center justify-center shadow-lg shadow-accent/20 animate-float animation-delay-400">
              <span className="text-3xl sm:text-4xl">💻</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Games;
